import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { startOfDay, endOfDay, subDays, format, differenceInDays, differenceInMinutes } from 'date-fns';
import { DateRangePicker, createStaticRanges, defaultStaticRanges } from 'react-date-range';

import Button from './Button';

const DEFAULT_KEY = 'selection';

const ADT_RANGES = defaultStaticRanges.map(({ label, range }) => ({ label, range }));
ADT_RANGES.unshift({
	label: 'Last 48 Hours',
	range: () => ({
		startDate: subDays(new Date(), 2),
		endDate: new Date()
	}),
});
ADT_RANGES.unshift({
	label: 'Last 24 Hours',
	range: () => ({
		startDate: subDays(new Date(), 1),
		endDate: new Date()
	}),
});
ADT_RANGES.push({
	label: 'Last 90 Days',
	range: () => ({
		startDate: subDays(startOfDay(new Date()), 90),
		endDate: endOfDay(new Date())
	}),
});
const STATIC_RANGES = createStaticRanges(ADT_RANGES);

const getButtonTitle = ({ startDate, endDate }) => {
	const diffInDays = differenceInDays(endDate, startDate);
	const diffToNow = differenceInMinutes(new Date(), endDate);
	if (diffInDays > 89) {
		return 'Last 90 Days';
	} else if (diffInDays < 2 && diffToNow < 30) {
		return 'Last 24 Hours';
	} else if (diffInDays < 3 && diffToNow < 30) {
		return 'Last 48 Hours';
	}
	return `${format(startDate, 'MM/dd/yyyy')} - ${format(endDate, 'MM/dd/yyyy')}`;
};

const DateRangePickerDropdown = ({ startDate, endDate, onRangeChosen, maxDate, minDate, position = 'left' }) => {
	const [isOpen, setOpen] = useState(false);
	const [state, setState] = useState([{
		key: DEFAULT_KEY,
		startDate,
		endDate,
	}]);

	const handleToggle = () => {
		setOpen(!isOpen);
	};

	const handleChange = (item) => {
		if (item[DEFAULT_KEY]) {
			setState([item[DEFAULT_KEY]]);
		}
	};

	const handleCancel = () => {
		setOpen(false);
	};

	const handleApply = () => {
		onRangeChosen(state[0]);
		setOpen(false);
	};

	return (
		<div className={cn('dropdown', 'daterangepicker-dropdown', { 'open': isOpen })}>
			<Button
				leftIcon="calendar"
				title={getButtonTitle(state[0])}
				onClick={handleToggle}
				toggle
			/>
			<div className={cn('dropdown-menu', { 'dropdown-menu-right': position === 'right', 'dropdown-menu-center': position === 'center' })}>
				<DateRangePicker
					onChange={handleChange}
					showSelectionPreview={true}
					moveRangeOnFirstSelection={false}
					months={2}
					ranges={state}
					direction="horizontal"
					minDate={minDate}
					maxDate={maxDate}
					staticRanges={STATIC_RANGES}
					inputRanges={[]}
				/>
				<div className="daterangepicker-dropdown-footer">
					<Button
						title="Cancel"
						large
						onClick={handleCancel}
					/>
					<Button
						title="Apply"
						onClick={handleApply}
						large
						primary
						className="ml"
					/>
				</div>
			</div>
		</div>
	);
};

DateRangePickerDropdown.propTypes = {
	onRangeChosen: PropTypes.func,
	startDate: PropTypes.object,
	endDate: PropTypes.object,
	minDate: PropTypes.object,
	maxDate: PropTypes.object,
	position: PropTypes.oneOf(['left', 'center', 'right'])
};

export default DateRangePickerDropdown;
